(() => {
  const splitLines = (value = "") => value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);

  const iconPaths = {
    "arrow-right": ["M5 12h14", "M12 5l7 7-7 7"],
    check: ["m20 6-11 11-5-5"],
  };

  const createIcon = (name, size, strokeWidth = 2) => {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("width", String(size));
    svg.setAttribute("height", String(size));
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", String(strokeWidth));
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");
    svg.setAttribute("aria-hidden", "true");
    (iconPaths[name] || []).forEach((pathData) => {
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", pathData);
      svg.append(path);
    });
    return svg;
  };

  function initMobileNavigation(root) {
    root.querySelectorAll(".d2-mobile-panel a").forEach((link) => {
      if (link.dataset.d2Ready) return;
      link.dataset.d2Ready = "true";
      link.addEventListener("click", () => link.closest("details")?.removeAttribute("open"));
    });
  }

  function initLocalPageMirrors() {
    if (!['127.0.0.1', 'localhost'].includes(window.location.hostname)) return;
    if (document.documentElement.dataset.d2LocalRoutes) return;
    document.documentElement.dataset.d2LocalRoutes = 'true';
    const mirrors = {
      '/pages/men': '/search?view=men',
      '/pages/women': '/search?view=women',
      '/pages/about': '/search?view=about',
      '/pages/science': '/search?view=science',
      '/pages/the-box': '/search?view=plan',
      '/pages/food-guide': '/search?view=food-guide',
      '/pages/hair-profile': '/search?view=quiz',
    };
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      if (!link || link.target || link.hasAttribute('download')) return;
      const url = new URL(link.href, window.location.href);
      const mirror = mirrors[url.pathname];
      if (!mirror || url.origin !== window.location.origin) return;
      event.preventDefault();
      window.location.assign(`${mirror}${url.hash}`);
    });
  }

  function initTabs(root) {
    root.querySelectorAll("[data-d2-tabs]").forEach((tabs) => {
      if (tabs.dataset.d2Ready) return;
      tabs.dataset.d2Ready = "true";
      const buttons = [...tabs.querySelectorAll("[data-d2-tab]")];
      const panels = [...tabs.querySelectorAll("[data-d2-tab-panel]")];
      buttons.forEach((button) => {
        button.addEventListener("click", () => {
          const target = button.dataset.d2Tab;
          buttons.forEach((item) => {
            const active = item === button;
            item.classList.toggle("is-active", active);
            item.classList.toggle("active", active);
            item.setAttribute("aria-selected", String(active));
            item.setAttribute("aria-pressed", String(active));
          });
          panels.forEach((panel) => panel.classList.toggle("is-active", panel.dataset.d2TabPanel === target));
        });
      });
    });
  }

  function initPatternExplorers(root) {
    root.querySelectorAll("[data-d2-pattern]").forEach((explorer) => {
      if (explorer.dataset.d2Ready) return;
      explorer.dataset.d2Ready = "true";
      const buttons = [...explorer.querySelectorAll(".pattern-tabs button")];
      const image = explorer.querySelector(".pattern-image img");
      const source = explorer.querySelector(".pattern-image source");
      const label = explorer.querySelector(".pattern-copy .kicker");
      const heading = explorer.querySelector(".pattern-copy h3");
      const copy = explorer.querySelector(".d2-pattern-copy");
      buttons.forEach((button) => {
        button.addEventListener("click", () => {
          buttons.forEach((item) => {
            const active = item === button;
            item.classList.toggle("active", active);
            item.setAttribute("aria-selected", String(active));
          });
          if (image) {
            image.src = button.dataset.image || image.src;
            image.alt = button.dataset.alt || "";
          }
          if (source) source.srcset = button.dataset.image || source.srcset;
          if (label) label.textContent = button.dataset.label || "";
          if (heading) heading.textContent = button.dataset.heading || "";
          if (copy) copy.textContent = button.dataset.copy || "";
        });
      });
    });
  }

  function initSimulations(root) {
    root.querySelectorAll("[data-d2-simulation]").forEach((simulation) => {
      if (simulation.dataset.d2Ready) return;
      simulation.dataset.d2Ready = "true";
      const range = simulation.querySelector('input[type="range"]');
      const buttons = [...simulation.querySelectorAll(".range-labels button")];
      const strip = simulation.querySelector(".simulation-strip");
      const badgeStage = simulation.querySelector(".simulation-badge span");
      const badgeMonth = simulation.querySelector(".simulation-badge strong");
      const controlMonth = simulation.querySelector(".simulation-control-heading strong");
      const heading = simulation.querySelector(".simulation-copy h3");
      const desktopCopy = simulation.querySelector(".d2-simulation-copy");
      const mobileCopy = simulation.querySelector(".d2-simulation-copy-mobile");

      const setStage = (nextIndex) => {
        const index = Math.max(0, Math.min(buttons.length - 1, Number(nextIndex) || 0));
        const button = buttons[index];
        if (!button) return;
        if (range) range.value = String(index);
        if (strip) strip.style.setProperty("--simulation-stage", String(index));
        if (badgeStage) badgeStage.textContent = `Illustrative stage 0${index + 1}`;
        if (badgeMonth) badgeMonth.textContent = button.dataset.month || "";
        if (controlMonth) controlMonth.textContent = button.dataset.month || "";
        if (heading) heading.textContent = button.dataset.heading || "";
        if (desktopCopy) desktopCopy.textContent = button.dataset.copy || "";
        if (mobileCopy) mobileCopy.textContent = button.dataset.copy || "";
        buttons.forEach((item, itemIndex) => item.classList.toggle("active", itemIndex === index));
      };

      range?.addEventListener("input", () => setStage(range.value));
      buttons.forEach((button, index) => button.addEventListener("click", () => setStage(index)));
      setStage(0);
    });
  }

  function initJournals(root) {
    root.querySelectorAll("[data-d2-journal]").forEach((journal) => {
      if (journal.dataset.d2Ready) return;
      journal.dataset.d2Ready = "true";
      const buttons = [...journal.querySelectorAll(".d2-review-controls button")];
      const label = journal.querySelector(".d2-review-profile span");
      const heading = journal.querySelector(".d2-review-profile h3");
      const setSubject = (subject) => {
        journal.querySelectorAll(".d2-review-sequence img").forEach((image) => {
          image.src = image.dataset[subject] || image.src;
          image.alt = `Generated ${subject === "women" ? "women's center-part" : "men's crown-density"} checkpoint at ${image.dataset.month || ""}`.trim();
        });
        if (label) label.textContent = subject === "women" ? "Part journal" : "Crown journal";
        if (heading) heading.textContent = subject === "women" ? "A center-part review" : "A crown-density review";
        buttons.forEach((button) => {
          const active = button.dataset.subject === subject;
          button.classList.toggle("active", active);
          button.setAttribute("aria-selected", String(active));
        });
      };
      buttons.forEach((button) => button.addEventListener("click", () => setSubject(button.dataset.subject || "men")));
    });
  }

  function initScienceStatus(root) {
    root.querySelectorAll("[data-d2-science-status]").forEach((section) => {
      if (section.dataset.d2Ready) return;
      section.dataset.d2Ready = "true";
      const buttons = [...section.querySelectorAll(".science-status-tabs button")];
      const panel = section.querySelector(".science-status-explorer article");
      const heading = panel?.querySelector("h3");
      const copy = panel?.querySelector(".d2-status-copy");
      const proof = panel?.querySelector(".d2-status-proof");
      const action = panel?.querySelector(".d2-status-action");
      const meter = [...(panel?.querySelectorAll(".science-status-meter i") || [])];
      buttons.forEach((button) => {
        button.addEventListener("click", () => {
          buttons.forEach((item) => {
            const active = item === button;
            item.classList.toggle("active", active);
            item.setAttribute("aria-selected", String(active));
          });
          if (heading) heading.textContent = button.dataset.label || "";
          if (copy) copy.textContent = button.dataset.copy || "";
          if (proof) proof.textContent = button.dataset.proof || "";
          if (action) action.textContent = button.dataset.action || "";
          const level = Number(button.dataset.level || 0);
          meter.forEach((item, index) => item.classList.toggle("filled", index < level));
          panel?.querySelector(".science-status-meter")?.setAttribute("aria-label", `${level} out of 5 evidence confidence`);
        });
      });
    });
  }

  function initCycleExplorers(root) {
    root.querySelectorAll("[data-d2-cycle]").forEach((cycle) => {
      if (cycle.dataset.d2Ready) return;
      cycle.dataset.d2Ready = "true";
      const buttons = [...cycle.querySelectorAll(".cycle-buttons button")];
      const image = cycle.querySelector(".cycle-image img");
      const source = cycle.querySelector(".cycle-image source");
      const kicker = cycle.querySelector(".cycle-panel > .kicker");
      const heading = cycle.querySelector(".cycle-panel > h3");
      const copy = cycle.querySelector(".cycle-panel > p:last-child");

      buttons.forEach((button) => {
        button.addEventListener("click", () => {
          buttons.forEach((item) => {
            const active = item === button;
            item.classList.toggle("active", active);
            item.setAttribute("aria-pressed", String(active));
          });
          if (source) source.srcset = button.dataset.mobileImage || button.dataset.image || source.srcset;
          if (image) {
            image.src = button.dataset.image || image.src;
            image.alt = button.dataset.alt || "";
            image.style.animation = "none";
            void image.offsetHeight;
            image.style.animation = "";
          }
          if (kicker) kicker.textContent = button.dataset.eyebrow || "";
          if (heading) heading.textContent = button.dataset.heading || "";
          if (copy) copy.textContent = button.dataset.copy || "";
        });
      });
    });
  }

  function initInstagramRails(root) {
    root.querySelectorAll('[data-d2-instagram-rail]').forEach((rail) => {
      if (rail.dataset.d2Ready) return;
      rail.dataset.d2Ready = 'true';
      const track = rail.querySelector('[data-d2-instagram-track]');
      const previous = rail.querySelector('[data-d2-instagram-prev]');
      const next = rail.querySelector('[data-d2-instagram-next]');
      if (!track) return;
      const updateControls = () => {
        const remaining = track.scrollWidth - track.clientWidth;
        if (previous) previous.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= remaining - 2;
      };
      const move = (direction) => {
        const card = track.querySelector('.d2-about-instagram-card');
        const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
        const distance = card ? card.getBoundingClientRect().width + gap : track.clientWidth * .8;
        track.scrollBy({ left: direction * distance, behavior: 'smooth' });
      };
      previous?.addEventListener('click', () => move(-1));
      next?.addEventListener('click', () => move(1));
      track.addEventListener('scroll', updateControls, { passive: true });
      window.addEventListener('resize', updateControls, { passive: true });
      updateControls();
    });
  }

  function initQuizzes(root) {
    root.querySelectorAll('[data-d2-quiz]').forEach((quiz) => {
      if (quiz.dataset.d2Ready) return;
      quiz.dataset.d2Ready = 'true';
      const parseOptions = (raw) => splitLines(raw).map((line) => {
        const [id, label, route] = line.split('|').map((part) => part.trim());
        return { id, label: label || id, route: route || '' };
      });
      const questions = [...quiz.querySelectorAll('[data-d2-question]')].map((node) => ({
        key: node.dataset.key, stage: node.dataset.stage, title: node.dataset.title,
        helper: node.dataset.helper, kind: node.dataset.kind || 'single', required: node.dataset.required !== 'false',
        placeholder: node.dataset.placeholder, showFor: node.dataset.showFor || 'all',
        options: parseOptions(node.dataset.options || ''), menOptions: parseOptions(node.dataset.menOptions || ''), womenOptions: parseOptions(node.dataset.womenOptions || '')
      }));
      const answers = new Map();
      let position = -1;
      let editorRoute = '';
      const shell = quiz.querySelector('[data-quiz-view="questions"]');
      const grid = shell.querySelector('.answer-grid');
      const heading = shell.querySelector('.d2-quiz-question');
      heading.tabIndex = -1;
      const route = () => editorRoute || [...answers.values()].flat().find((answer) => ['men', 'women', 'general'].includes(answer.route))?.route || 'general';
      const activeQuestions = () => questions.filter((q) => q.showFor === 'all' || q.showFor === route());
      const optionsFor = (q) => route() === 'men' && q.menOptions.length ? q.menOptions : route() === 'women' && q.womenOptions.length ? q.womenOptions : q.options;
      const show = (view) => {
        quiz.querySelectorAll('[data-quiz-view]').forEach((node) => { node.hidden = node.dataset.quizView !== view; });
      };
      const showBox = (audience) => quiz.querySelectorAll('[data-quiz-box]').forEach((node) => { node.hidden = node.dataset.quizBox !== audience; });
      const selectedRoutes = () => [...answers.values()].flat().map((answer) => answer.route);
      const selectedFormula = () => {
        const routes = selectedRoutes();
        if (route() === 'men' && routes.includes('review_foundation')) return 'foundation';
        if (routes.includes('review_focus')) return 'focus';
        if (routes.includes('focus')) return 'focus';
        if (routes.includes('foundation')) return 'foundation';
        return '';
      };
      const selectedBox = () => {
        const audience = route();
        const formula = selectedFormula();
        return ['men', 'women'].includes(audience) && formula ? `${audience}s-${formula}` : 'general';
      };
      const finish = () => {
        const routes = selectedRoutes();
        quiz.querySelectorAll('[data-review-box]').forEach((node) => { node.hidden = true; });
        // Warning signs always win; medication routes never unlock a purchase.
        if (routes.includes('care')) { show('care'); }
        else if (routes.some((value) => ['review', 'review_focus', 'review_foundation'].includes(value))) {
          show('review'); showBox('none');
          const boxKey = selectedBox();
          quiz.querySelectorAll('[data-review-box]').forEach((node) => { node.hidden = node.dataset.reviewBox !== boxKey; });
          const box = [...quiz.querySelectorAll('[data-quiz-box]')].find((node) => node.dataset.quizBox === boxKey);
          const candidate = quiz.querySelector('[data-review-candidate]');
          candidate.textContent = box?.dataset.boxTitle ? `For discussion: ${box.dataset.boxTitle}` : 'Discuss the right next step with your licensed clinician.';
          quiz.querySelector('[data-review-reason]').textContent = routes.includes('review_foundation') && route() === 'men'
            ? 'Your SSRI answer takes priority over the general medication route. Men’s Foundation is a discussion option only; do not start it without your prescribing clinician’s approval.'
            : 'Your medication answer needs an individual interaction review. Any box shown here is a discussion option only, not medical clearance.';
          const ingredients = quiz.querySelector('[data-review-ingredients]');
          const source = [...quiz.querySelectorAll('[data-review-formula]')].find((node) => node.dataset.reviewFormula === boxKey);
          ingredients.replaceChildren(...(source ? [...source.childNodes].map((node) => node.cloneNode(true)) : [document.createTextNode('Bring the complete formula and your medication list to your licensed clinician.')]));
          quiz.querySelector('[data-review-ack]').checked = false;
          quiz.querySelector('[data-review-ack-status]').textContent = '';
        }
        else {
          show('results'); showBox(selectedBox());
          quiz.querySelectorAll('[data-choice-audience]').forEach((node) => { node.hidden = route() !== 'general' && node.dataset.choiceAudience !== route(); });
          quiz.querySelector('.answer-tags').replaceChildren(...[...answers.values()].flat().map((answer) => {
            const tag = document.createElement('span'); tag.textContent = answer.label; return tag;
          }));
        }
        const target = quiz.querySelector('[data-quiz-view]:not([hidden]) h1');
        if (target) { target.tabIndex = -1; target.focus(); }
        window.scrollTo({top: 0, behavior: 'auto'});
      };
      const render = () => {
        const active = activeQuestions();
        if (position < 0) { show('welcome'); return; }
        if (position >= active.length) { finish(); return; }
        const q = active[position];
        show('questions');
        shell.querySelector('.quiz-count').textContent = `Question ${position + 1} of ${active.length}`;
        shell.querySelector('.d2-quiz-stage').textContent = q.stage;
        heading.textContent = q.title;
        shell.querySelector('.d2-quiz-helper').textContent = q.helper;
        const list = shell.querySelector('ol');
        list.replaceChildren(...active.map((item, index) => {
          const li = document.createElement('li'); li.className = index === position ? 'active' : index < position ? 'done' : '';
          if (index === position) li.setAttribute('aria-current','step');
          const marker = document.createElement('span'); marker.textContent = String(index + 1);
          li.append(marker, document.createTextNode(item.stage)); return li;
        }));
        const previous = answers.get(q.key) || [];
        let selected = [...previous];
        let committed = false;
        const save = () => {
          if (committed) return;
          if (q.required && !selected.length) { error.textContent = quiz.dataset.requiredMessage; return; }
          committed = true;
          // Editing any earlier answer clears downstream answers, including hidden branches.
          questions.slice(questions.indexOf(q) + 1).forEach((item) => answers.delete(item.key));
          answers.set(q.key, selected); position += 1; render();
        };
        grid.replaceChildren();
        const error = document.createElement('p'); error.setAttribute('role','alert');
        if (['single', 'multiple'].includes(q.kind)) {
          optionsFor(q).forEach((option) => {
            const button = document.createElement('button'); button.type = 'button';
            button.textContent = option.label; button.setAttribute('aria-pressed', String(selected.some((s) => s.id === option.id)));
            button.addEventListener('click', () => {
              if (q.kind === 'single') { selected = [option]; save(); }
              else { selected = selected.some((s) => s.id === option.id) ? selected.filter((s) => s.id !== option.id) : [...selected, option]; button.setAttribute('aria-pressed', String(selected.some((s) => s.id === option.id))); }
            }); grid.append(button);
          });
        } else {
          const input = document.createElement(q.kind === 'textarea' ? 'textarea' : 'input');
          if (q.kind !== 'textarea') input.type = q.kind === 'number' ? 'number' : 'text';
          input.setAttribute('aria-label', q.title); input.placeholder = q.placeholder || ''; input.required = q.required;
          input.value = previous[0]?.label || ''; input.maxLength = 2000;
          input.addEventListener('input', () => { selected = input.value.trim() ? [{id: q.key, label: input.value.trim(), route: ''}] : []; });
          grid.append(input);
        }
        if (q.kind !== 'single' || !q.required || !optionsFor(q).length) {
          const next = document.createElement('button'); next.type = 'button'; next.className = 'primary-button'; next.textContent = quiz.dataset.continueLabel || 'Continue'; next.addEventListener('click', save); grid.append(next);
        }
        grid.append(error); heading.focus();
      };
      quiz.querySelector('[data-quiz-start]').addEventListener('click', () => { editorRoute = ''; position = 0; render(); });
      quiz.querySelector('[data-quiz-back]').addEventListener('click', () => { position -= 1; render(); });
      quiz.querySelectorAll('[data-quiz-reset]').forEach((button) => button.addEventListener('click', () => { answers.clear(); editorRoute = ''; position = -1; render(); }));
      quiz.querySelectorAll('[data-choose-box]').forEach((button) => button.addEventListener('click', () => showBox(button.dataset.chooseBox)));
      quiz.querySelector('[data-review-ack]')?.addEventListener('change', (event) => {
        quiz.querySelector('[data-review-ack-status]').textContent = event.target.checked
          ? 'Acknowledged. Medical approval is still required before starting the box.' : '';
      });
      quiz.addEventListener('shopify:block:select', (event) => {
        if (!window.Shopify?.designMode) return;
        const q = questions.find((item) => item.key === event.detail?.blockId);
        if (!q) return;
        editorRoute = q.showFor === 'all' ? '' : q.showFor;
        position = activeQuestions().indexOf(q); render();
      });
      show('welcome');
    });
  }

  function init(root = document) {
    initLocalPageMirrors();
    initMobileNavigation(root);
    initTabs(root);
    initPatternExplorers(root);
    initSimulations(root);
    initJournals(root);
    initScienceStatus(root);
    initCycleExplorers(root);
    initInstagramRails(root);
    initQuizzes(root);
  }

  document.addEventListener("DOMContentLoaded", () => init());
  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
